if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      prefetchCache = new Map();
      prefetchAbortController = undefined;

      constructor() {
        super();
        this.retries = 0;

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initVariantPrefetch();
        this.initStickyColumnHeight();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        this.productId = this.querySelector('[name="product-id"]').value;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initVariantPrefetch() {
        if (this.dataset.enableVariantPrefetch !== 'true') return;

        this.prefetchVariants();
      }

      prefetchVariants() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const currentSelectedOptions = [...variantSelects.selectedOptionValues];
        const variantPickers = this.querySelectorAll(
          '.product-variant-picker.product-variant-picker_swatch:not(.in-transition)'
        );
        const requestUrls = [];

        variantPickers.forEach((picker) => {
          const pickerIndex = parseInt(picker.dataset.variantOption, 10);

          const currentColorInput = picker.querySelector(
            'input[type="radio"].swatch-input__input:checked:not([name="option-learn-more-tabs-colour"])'
          );
          if (currentColorInput) {
            const currentColorOptionValue = currentColorInput.dataset.optionValueId;
            const inputs = picker.querySelectorAll(
              'input[type="radio"]:not(:checked):not(:disabled):not(.unavailable input):not([name="option-learn-more-tabs-colour"])'
            );

            const selectedOptionsWithoutColor = currentSelectedOptions.filter(
              (option) => option !== currentColorOptionValue
            );

            inputs.forEach((input) => {
              if (input.dataset.productUrl && input.dataset.productUrl.trim() !== '') return;
              const optionValueId = input.dataset.optionValueId;
              if (!optionValueId) return;

              const selectedOptionValues = [...selectedOptionsWithoutColor];
              selectedOptionValues.push(optionValueId);

              const productUrl = this.dataset.productUrl || this.dataset.url;
              const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, false);
              requestUrls.push(requestUrl);
            });
          }
        });

        const baseDelay = 400;
        const maxDelay = 4000;
        const backoffMultiplier = 1.5;

        requestUrls.forEach((requestUrl, index) => {
          const delay = Math.min(baseDelay * Math.pow(backoffMultiplier, index), maxDelay);
          setTimeout(() => {
            this.prefetchVariant(requestUrl);
          }, Math.round(delay));
        });
      }

      prefetchVariant(requestUrl) {
        if (this.prefetchCache.has(requestUrl)) return;

        this.prefetchCache.set(requestUrl, 'pending');

        const prefetchController = new AbortController();

        fetch(requestUrl, {
          signal: prefetchController.signal,
          priority: 'low',
        })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.prefetchCache.set(requestUrl, html);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              this.prefetchCache.delete(requestUrl);
            }
          });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.stickyColumnObserver?.disconnect();
        this.stickyColumnMediaQuery?.removeEventListener('change', this.onStickyColumnMediaChange);
      }

      // Exposes the sticky info column's height as --product-info-height, so section-main-product.css
      // can keep it top-aligned when it fits the screen and stick it by its bottom when it does not.
      // The height comes from the observer entry rather than offsetHeight, which forced a layout whenever an
      // earlier observer in the same frame had changed the DOM. The property is only used at >= 1200px and
      // restyles the whole column when set, so it is only written on desktop, and only when it changes.
      initStickyColumnHeight() {
        const column = this.querySelector('.product__column-sticky');
        if (!column || typeof ResizeObserver === 'undefined') return;

        const desktop = window.matchMedia('(min-width: 1200px)');
        let height = null;
        let written = null;
        const write = () => {
          if (height === null || height === written || !desktop.matches) return;
          written = height;
          column.style.setProperty('--product-info-height', `${height}px`);
        };

        this.stickyColumnObserver = new ResizeObserver(([entry]) => {
          const size = entry.borderBoxSize?.[0];
          height = size ? Math.round(size.blockSize) : column.offsetHeight;
          write();
        });
        this.stickyColumnObserver.observe(column);

        this.stickyColumnMediaQuery = desktop;
        this.onStickyColumnMediaChange = write;
        desktop.addEventListener('change', write);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const mediaGalleryEl = this.querySelector('product-media-gallery') || this.querySelector('product-media-gallery-v3');
        if (mediaGalleryEl) mediaGalleryEl.classList.remove('product-media-gallery--loaded');
        // Find the nearest section
        const nearestSection = target.closest('section.shopify-section') || target.closest('quick-buy-modal');
        // Add the 'loading' class when the request begins
        if (nearestSection) {
          nearestSection.classList.add('dynamic-content-loading');
          document.dispatchEvent(new CustomEvent('theme:loading:start'), {
            bubbles: true,
          });
        }
        // Everything above is the immediate feedback for the tap. The rest (form reset, request, and for a
        // prefetched variant the whole swap) waits until that feedback has painted, so it isn't part of the
        // interaction. A setTimeout(0) here often ran before the paint and landed in the presentation delay.
        afterNextPaint().then(() => {
          const hoverCardTriggerButton = this.querySelector('.hover-card-trigger__button');

          if (hoverCardTriggerButton) {
            hoverCardTriggerButton.disabled = true;
          }

          this.resetProductFormState();

          const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

          this.pendingRequestUrl = productUrl;
          const shouldSwapProduct = this.dataset.url !== productUrl;
          const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

          const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

          if (this.closest('quick-buy-modal')) {
            this.closest('quick-buy-modal').fetchProduct(requestUrl);
            return;
          }

          const callback = shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl);

          this.renderProductInfo({
            requestUrl,
            targetId: target.id,
            callback: (response, signal) => {
              // The swap runs over several frames, so it says when the content is in place. The loading state
              // clears then: once the gallery and dynamic content have been swapped, and before an unavailable
              // combination retries with another option (which sets the loading state again).
              let loadingCleared = false;
              const clearLoading = () => {
                if (loadingCleared || !nearestSection) return;
                loadingCleared = true;
                nearestSection.classList.remove('dynamic-content-loading');
                document.dispatchEvent(new CustomEvent('theme:loading:end'), {
                  bubbles: true,
                });
              };
              // A swap that throws part-way must not leave the section behind its loading overlay. An abort
              // means a newer request has taken over, and that request clears the loading state itself.
              return Promise.resolve(callback(response, signal, clearLoading)).catch((error) => {
                if (error?.name !== 'AbortError') clearLoading();
                throw error;
              });
            },
          });
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return async (html, signal, onContentSwapped = () => {}) => {
          onContentSwapped();
          this.productModal?.remove();

          this.prefetchCache.clear();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const newProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(newProductInfo);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          requestAnimationFrame(() => {
            this.initVariantPrefetch();
          });

          // Analytics and the URL update run third-party code (web pixels, history.replaceState hooks), so they
          // wait until the new product has painted. Same order as before: track, then update the URL.
          await afterNextPaint();
          this.trackProductViewed(variant, newProductInfo);
          await yieldToMain();
          this.updateURL(productUrl, variant?.id);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        // The callbacks are async and yield between steps, so a newer request can abort them part-way; the newer
        // response then redoes every step. Only a render that ran to the end restores focus and announces itself.
        const onRendered = () => {
          if (signal.aborted) return;
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
          document.dispatchEvent(
            new CustomEvent('product-info:loaded', {
              bubbles: true,
            })
          );
        };
        const onError = (error) => {
          if (error.name === 'AbortError') {
          } else {
            console.error(error);
          }
        };

        const cachedHtml = this.prefetchCache.get(requestUrl);

        if (cachedHtml && cachedHtml !== 'pending') {
          this.pendingRequestUrl = null;
          // Clone the cached HTML to prevent mutations from affecting the cache
          const clonedHtml = cachedHtml.cloneNode(true);
          Promise.resolve(callback(clonedHtml, signal)).then(onRendered).catch(onError);
          return;
        }

        fetch(requestUrl, { signal })
          .then((response) => response.text())
          .then(async (responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            // Parsing the section is a long task on its own; let pending input in before swapping it into the page.
            await yieldToMain();
            if (signal.aborted) return;
            await callback(html, signal);
          })
          .then(onRendered)
          .catch(onError);
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      trackProductViewed(variant, productInfoNode = null) {
        if (!variant) return;
        const source = productInfoNode ?? this;
        const currencyCode = document.body?.dataset?.currency || window.Shopify?.currency || 'USD';
        const amount =
          variant.price != null
            ? typeof variant.price === 'number'
              ? Number((variant.price / 100).toFixed(2))
              : Number(variant.price)
            : undefined;
        const imageSrc =
          variant.image?.src ?? variant.featured_media?.preview_image?.src ?? variant.featured_image?.src ?? '';
        const baseUrl = source.dataset?.url
          ? `${window.location.origin}${source.dataset.url}`
          : window.location.pathname;
        const properties = {
          productVariant: {
            price: {
              amount,
              currencyCode,
            },
          },
          product: {
            title: source.dataset?.productTitle ?? '',
            vendor: source.dataset?.productVendor ?? '',
            id: source.dataset?.productId ?? source.productId ?? '',
            untranslatedTitle: source.dataset?.productUntranslatedTitle ?? source.dataset?.productTitle ?? '',
            url: baseUrl,
            type: source.dataset?.productType ?? '',
          },
          id: variant.id,
          image: { src: imageSrc },
          sku: variant.sku ?? '',
          title: variant.title ?? '',
          untranslatedTitle: variant.untranslated_title ?? variant.title ?? '',
        };
        if (window.polar) {
          window.polar.track('product_viewed', properties);
        }
        if (window.Shopify?.analytics) {
          window.Shopify.analytics.publish('variant_viewed', properties);
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length && !shouldFetchFullPage) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html, previousScrollY = window.scrollY) {
        const variantSelects = html.querySelectorAll('.product-variant-picker');
        if (variantSelects.length) {
          // Each viewTransition used to read scrollY and scrollTo() back, a forced style + layout pass per picker.
          // Swap them all, then restore the scroll once in the next frame, where that layout is the frame's own.
          variantSelects.forEach((variantSelect) => {
            const target = document.querySelector(`[data-variant-option="${variantSelect.dataset.variantOption}"]`);
            HTMLUpdateUtility.viewTransition(target, variantSelect, this.preProcessHtmlCallbacks, [], {
              restoreScroll: false,
            });
          });

          requestAnimationFrame(() => {
            // instant, to override the smooth scrolling set in CSS
            window.scrollTo({ top: previousScrollY, behavior: 'instant' });
          });

          requestAnimationFrame(() => {
            this.initVariantPrefetch();
          });
        }
      }

      processAccordions(html, type) {
        // We'll build a convenient selector and use getAttribute for consistency
        const dataAttr = `data-dynamic-variant-accordion-${type}`;

        // All existing accordions in the live DOM that match our data attribute
        const currentAccordions = Array.from(document.querySelectorAll(`[${dataAttr}]`));

        // Figure out which accordions are currently open
        const openAccordions = currentAccordions
          .filter((accordion) => {
            const details = accordion.querySelector('details');
            return details && details.open;
          })
          .map((accordion) => accordion.getAttribute(dataAttr));

        // All accordions in the *newly fetched* HTML snippet
        const newAccordions = Array.from(html.querySelectorAll(`[${dataAttr}]`));

        // 1. Remove any *current* accordions that do not exist in *new* accordions
        currentAccordions.forEach((existing) => {
          const oldKey = existing.getAttribute(dataAttr);
          const stillExists = newAccordions.some((acc) => acc.getAttribute(dataAttr) === oldKey);
          if (!stillExists) {
            existing.remove();
          }
        });

        // 2. Insert/replace each new accordion into the DOM
        let previousSibling = null;

        newAccordions.forEach((newAccordion) => {
          const key = newAccordion.getAttribute(dataAttr);

          // If this accordion was open previously, keep it open
          if (openAccordions.includes(key)) {
            const details = newAccordion.querySelector('details');
            if (details) {
              details.setAttribute('open', '');
              details.classList.add('accordion-details--open');
            }
          }

          // Find if an accordion with the same key is already in the live DOM
          const existingInDOM = document.querySelector(`[${dataAttr}="${key}"]`);

          if (existingInDOM) {
            // Directly replace the old accordion with the new one
            existingInDOM.replaceWith(newAccordion);
            previousSibling = newAccordion;
          } else {
            // If we have a previous sibling, place after it in the DOM
            if (previousSibling) {
              previousSibling.after(newAccordion);
              previousSibling = newAccordion;
            } else {
              // Otherwise, insert after the "accordion-dynamic-content-{type}" marker.
              // If you want to *append* inside a container instead, you can do target.append(newAccordion).
              const target = document.querySelector(`.accordion-dynamic-content-${type}`);
              if (target) {
                target.after(newAccordion);
                previousSibling = newAccordion;
              }
            }
          }
        });
      }

      handleDynamicContent(html) {
        // Update storefront product
        const storefrontProducts = html.querySelectorAll('[is="storefront-product"]');
        storefrontProducts.forEach((element) => {
          const id = element.id;
          if (document.getElementById(id)) {
            const currentProduct = document.getElementById(id);
            const newProduct = element;
            Object.entries(newProduct.dataset).forEach(([key, value]) => {
              if (currentProduct.dataset[key] !== value) {
                currentProduct.dataset[key] = value;
              }
            });
          }
        });

        // Define selectors for the "Outside Inside" approach
        // These selectors identify containers that should have their dynamic content updated first,
        // before updating dynamic content outside of these containers
        // Add more selectors to this array as needed for additional inside/outside priority containers
        const insideContainerSelectors = ['specification-drawer'];

        // Index the live [data-dynamic-id] elements once instead of running a document-wide query per element. Each
        // index keeps the first match in document order, which is what document.querySelector() returned.
        const indexLive = (selector, keep = () => true) => {
          const index = new Map();
          document.querySelectorAll(selector).forEach((element) => {
            const id = element.dataset.dynamicId;
            if (!index.has(id) && keep(element)) index.set(id, element);
          });
          return index;
        };
        const insideIndexes = new Map(
          insideContainerSelectors.map((selector) => [selector, indexLive(`${selector} [data-dynamic-id]`)])
        );
        const outsideIndex = indexLive(
          '[data-dynamic-id]',
          (element) => !insideContainerSelectors.some((selector) => element.parentElement?.closest(selector))
        );

        // Carousels whose slides were replaced. Resetting scrollLeft forces a layout, so it waits for the next frame,
        // where that layout is the frame's own, instead of running after each replacement.
        const replacedCarousels = [];

        // Helper function to update dynamic content with context-specific selectors
        const updateDynamicElementWithContext = (element, isInside, containerSelector = null) => {
          const dynamicId = element.dataset.dynamicId;

          // Construct selector to find the correct matching element in the live DOM
          let targetSelector;
          let index;
          if (isInside && containerSelector) {
            // Find elements with this data-dynamic-id that are inside the same container type in live DOM
            targetSelector = `${containerSelector} [data-dynamic-id="${dynamicId}"]`;
            index = insideIndexes.get(containerSelector);
          } else {
            // Find elements with this data-dynamic-id that are NOT inside any specified containers in live DOM
            const excludeSelectors = insideContainerSelectors.map((selector) => `:not(${selector} *)`).join('');
            targetSelector = `[data-dynamic-id="${dynamicId}"]${excludeSelectors}`;
            index = outsideIndex;
          }

          // An earlier update can replace an indexed element (nested dynamic content) or add a new one, so fall back
          // to the query when the index has nothing connected.
          const indexed = index.get(dynamicId);
          const liveContent = indexed?.isConnected ? indexed : document.querySelector(targetSelector);
          if (liveContent) {
            liveContent.innerHTML = element.innerHTML;
            if (liveContent.querySelector('[data-carousel-item]')) replacedCarousels.push(liveContent);
          }
        };

        // INSIDE approach: Update dynamic content within specified containers
        // Ensures inside elements only update corresponding inside elements in the live DOM
        insideContainerSelectors.forEach((containerSelector) => {
          const containers = html.querySelectorAll(containerSelector);
          containers.forEach((container) => {
            const insideDynamicContent = container.querySelectorAll('[data-dynamic-id]');
            insideDynamicContent.forEach((element) => {
              updateDynamicElementWithContext(element, true, containerSelector);
            });
          });
        });

        // OUTSIDE approach: Update dynamic content outside specified containers
        // Ensures outside elements only update corresponding outside elements in the live DOM
        const allDynamicContent = html.querySelectorAll('[data-dynamic-id]');
        allDynamicContent.forEach((element) => {
          const isInsideSpecialContainer = insideContainerSelectors.some((containerSelector) => {
            return element.closest(containerSelector) !== null;
          });

          // Only process outside elements here
          if (!isInsideSpecialContainer) {
            updateDynamicElementWithContext(element, false);
          }
        });

        if (replacedCarousels.length) {
          requestAnimationFrame(() => {
            replacedCarousels.forEach((carousel) => {
              carousel.scrollLeft = 0;
              const swiper = carousel.closest('css-swiper');
              if (swiper) {
                swiper.removeExternalControlListeners();
                swiper.initExternalControls();
              }
            });
          });
        }

        this.processAccordions(html, 'metafields');
        this.processAccordions(html, 'notifications');
      }

      setFirstAvailableVariant(html) {
        const variantPickers = Array.from(html.querySelectorAll('.product-variant-picker')).reverse();
        variantPickers.forEach((variantPicker) => {
          const selectedOption = Array.from(
            variantPicker.querySelectorAll(
              'select:not(.exclude-first-available-variant) option[selected]:not(.disabled), fieldset input:checked:not(.disabled)'
            )
          )
            .filter((element) => !element.closest('.in-transition'))
            .filter((element) => !element.closest('.external-selects'))
            .map(({ dataset }) => dataset.optionValueId);

          if (!selectedOption.length) {
            const currentVariantPicker = document.getElementById(variantPicker.id);
            const firstOption = variantPicker.querySelector(
              'select:not(.exclude-first-available-variant) option:not(.disabled), fieldset input:not(.disabled)'
            );
            const currentFirstOption = currentVariantPicker.querySelector(
              `[data-option-value-id="${firstOption.dataset.optionValueId}"]`
            );
            if (currentFirstOption) {
              currentFirstOption.click();
            }
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        // Runs in three steps, each after the previous one has painted, so no single frame carries the whole swap:
        // 1. what the shopper is waiting for: option pickers, price, stock, ATC state and the variant id the form submits
        // 2. the rest of the variant's content: gallery, dynamic blocks and accordions
        // 3. notifications that mostly run third-party code, one task each: URL (history hooks), id input change event,
        //    then analytics and variantChange
        return async (html, signal, onContentSwapped = () => {}) => {
          // Read before anything below dirties the layout
          const previousScrollY = window.scrollY;
          const variant = this.getSelectedVariant(html);

          this.toggleLoadersByVariantId(variant?.id);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html, previousScrollY);

          if (!variant) {
            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);
            // Toggle again because the html content is updated
            this.toggleLoadersByVariantId(variant?.id);
            onContentSwapped();

            if (this.retries < 1) {
              this.retries++;
              this.setFirstAvailableVariant(html);
            } else {
              this.setUnavailable();
            }
            return;
          }

          this.retries = 0;
          this.updateVariantInputs(variant.id, { notify: false });

          // Toggle again because the html content is updated
          this.toggleLoadersByVariantId(variant.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const priceData = html.querySelector(`#Price-${this.sectionId} .price-item--regular`);
          const price = priceData ? priceData.innerHTML : '';

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Drop Mechanic
          const dropMechanicSource = html.getElementById(`drop-mechanic-${this.sectionId}`);
          const destination = this.querySelector(`#drop-mechanic-${this.dataset.section}`);
          if (dropMechanicSource.children.length && destination.children.length) {
            destination.querySelector('.product__drop-mechanic-progress-bar-title-wrapper').innerHTML =
              dropMechanicSource.querySelector('.product__drop-mechanic-progress-bar-title-wrapper').innerHTML;

            const progressBarSource = dropMechanicSource.querySelector('.product__drop-mechanic-progress-bar');
            const progressBarDestination = destination.querySelector('.product__drop-mechanic-progress-bar');
            if (progressBarSource && !progressBarDestination) {
              destination.append(progressBarSource);
            } else if (!progressBarSource && progressBarDestination) {
              progressBarDestination.remove();
            }
          } else if (dropMechanicSource.children.length && !destination.children.length) {
            destination.innerHTML = dropMechanicSource.innerHTML;
          } else if (!dropMechanicSource.children.length) {
            Array.from(destination.children).forEach((child) => child.remove());
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.handleNotificationButton(html.getElementById(`ProductSubmitButton-${this.sectionId}`));

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            html.getElementById(`ProductSubmitButton-${this.sectionId}`).hasAttribute('disabled') === true
              ? window.variantStrings.soldOut
              : `${window.variantStrings.addToCart} ${
                  price ? `• ${price === 0 ? window.variantStrings.free : price}` : ''
                }`
          );

          await afterNextPaint();
          if (signal?.aborted) return;

          // Only process dynamic content (including accordions) when we have a valid variant
          this.handleDynamicContent(html);
          onContentSwapped();

          // Each of these mostly runs third-party listeners, so they get a task each rather than one long one
          await afterNextPaint();
          if (signal?.aborted) return;
          this.updateURL(productUrl, variant.id);

          await yieldToMain();
          if (signal?.aborted) return;
          // Same value again (the dynamic content may have replaced an input), this time firing the change events
          this.updateVariantInputs(variant.id);

          await yieldToMain();
          if (signal?.aborted) return;
          this.trackProductViewed(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
              product: {
                id: this.productId,
              },
            },
          });
        };
      }

      // { notify: false } sets the values without firing change events. Those bubble to document-level
      // third-party listeners, so the variant swap sets the id first and fires the events once it has painted.
      updateVariantInputs(variantId, { notify = true } = {}) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            if (notify) input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(variantId) {
        const mediaGallery = this.querySelector('product-media-gallery') || this.querySelector('product-media-gallery-v3');
        if (mediaGallery?.updateMediaGallery) mediaGallery.updateMediaGallery(variantId);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      toggleLoadersByVariantId(variantId) {
        const loaders = document.querySelectorAll(
          '.product-media-gallery__variant-thumbnail .lazy-image, product-media-gallery .line-loader, product-media-gallery-v3 .line-loader, .product-variant-picker .line-loader'
        );

        if (!variantId) {
          loaders.forEach((loader) => {
            loader.classList.add('loading');
          });
        } else {
          loaders.forEach((loader) => {
            loader.classList.remove('loading');
          });
        }
      }

      get productForm() {
        return this.querySelector(`product-form.primary__product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
